import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePortalAuthStore } from '@/stores/portalAuth'
import { useUiStore } from '@/stores/ui'
import { trackPageView } from '@/utils/amplitude'

declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean
    adminOnly?: boolean
    excludeViewer?: boolean
    portal?: boolean
    reportPresentation?: boolean
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/views/AuthCallbackView.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
    },
    {
      path: '/management-reports',
      name: 'management-reports',
      component: () => import('@/views/ManagementReportsView.vue'),
    },
    {
      path: '/management-reports/new',
      name: 'management-report-create',
      component: () => import('@/views/ManagementReportFormView.vue'),
    },
    {
      path: '/management-reports/:id/present',
      name: 'management-report-present',
      component: () => import('@/views/ManagementReportDetailView.vue'),
      meta: { reportPresentation: true },
    },
    {
      path: '/management-reports/:id',
      name: 'management-report-detail',
      component: () => import('@/views/ManagementReportDetailView.vue'),
    },
    {
      path: '/contracts',
      name: 'contracts',
      component: () => import('@/views/ContractsView.vue'),
    },
    {
      path: '/contracts/:id',
      name: 'contract-detail',
      component: () => import('@/views/ContractDetailView.vue'),
      props: true,
    },
    {
      path: '/contracts/:id/meeting-minutes/new',
      name: 'meeting-minute-create',
      component: () => import('@/views/MeetingMinuteFormView.vue'),
    },
    {
      path: '/contracts/:id/meeting-minutes/:minuteId',
      name: 'meeting-minute-detail',
      component: () => import('@/views/MeetingMinuteDetailView.vue'),
      props: (route) => ({
        contractId: route.params.id as string,
        id: route.params.minuteId as string,
      }),
    },
    {
      path: '/contracts/:id/meeting-minutes/:minuteId/edit',
      name: 'meeting-minute-edit',
      component: () => import('@/views/MeetingMinuteFormView.vue'),
    },
    {
      path: '/contracts/:id/measurements',
      name: 'contract-measurements',
      component: () => import('@/views/ContractMeasurementsView.vue'),
      props: true,
    },
    {
      path: '/contracts/:id/kpis',
      name: 'contract-kpis',
      component: () => import('@/views/ContractKpisView.vue'),
      props: true,
    },
    {
      path: '/suppliers',
      name: 'suppliers',
      component: () => import('@/views/SuppliersView.vue'),
    },
    {
      path: '/action-plans',
      name: 'action-plans',
      component: () => import('@/views/ActionPlansView.vue'),
    },
    {
      path: '/exports',
      name: 'exports',
      component: () => import('@/views/ExportsView.vue'),
    },
    {
      path: '/action-plans/:id',
      name: 'action-plan-detail',
      component: () => import('@/views/ActionPlanDetailView.vue'),
      props: true,
    },
    {
      path: '/suppliers/:id',
      name: 'supplier-detail',
      component: () => import('@/views/SupplierDetailView.vue'),
      props: true,
    },
    {
      path: '/kpi-families',
      name: 'kpi-families',
      component: () => import('@/views/KpiFamiliesView.vue'),
    },
    {
      path: '/kpi-labels',
      name: 'kpi-labels',
      component: () => import('@/views/KpiLabelsView.vue'),
      meta: { adminOnly: true },
    },
    {
      path: '/reajustes-pendientes',
      name: 'reajustes-pendientes',
      component: () => import('@/views/ReajustesPendingView.vue'),
      meta: { excludeViewer: true },
    },
    {
      path: '/measurement-requests',
      name: 'measurement-requests',
      component: () => import('@/views/MeasurementRequestsView.vue'),
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: () => import('@/views/NotificationsView.vue'),
    },
    {
      path: '/settings/preferences',
      name: 'user-preferences',
      component: () => import('@/views/UserPreferencesView.vue'),
    },
    {
      path: '/settings/config',
      name: 'settings',
      component: () => import('@/views/SettingsView.vue'),
      meta: { adminOnly: true },
    },
    {
      path: '/settings/sso',
      redirect: { name: 'settings', query: { tab: 'sso' } },
    },
    {
      path: '/settings/users',
      name: 'users',
      component: () => import('@/views/UsersView.vue'),
      meta: { adminOnly: true },
    },
    {
      // Accesos se unifico dentro de Usuarios (OPSENG-3106); la ruta vieja
      // sobrevive solo para no romper bookmarks y links ya repartidos.
      path: '/settings/accesses',
      redirect: { name: 'users' },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPasswordView.vue'),
      meta: { public: true },
    },
    {
      path: '/reset-password/:token',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { public: true },
    },
    // Portal routes
    {
      path: '/portal/login',
      name: 'portal-login',
      component: () => import('@/views/PortalLoginView.vue'),
      meta: { public: true, portal: true },
    },
    {
      path: '/portal/auth/callback',
      name: 'portal-auth-callback',
      component: () => import('@/views/PortalAuthCallbackView.vue'),
      meta: { public: true, portal: true },
    },
    {
      path: '/portal',
      name: 'portal-dashboard',
      component: () => import('@/views/PortalDashboardView.vue'),
      meta: { portal: true },
    },
    {
      path: '/portal/contracts/:id',
      name: 'portal-contract-detail',
      component: () => import('@/views/PortalContractDetailView.vue'),
      props: true,
      meta: { portal: true },
    },
    {
      path: '/portal/meeting_minutes/:id',
      name: 'portal-meeting-minute-detail',
      component: () => import('@/views/PortalMeetingMinuteDetailView.vue'),
      props: true,
      meta: { portal: true },
    },
    {
      path: '/portal/action_plans/:id',
      name: 'portal-action-plan-detail',
      component: () => import('@/views/PortalActionPlanDetailView.vue'),
      props: true,
      meta: { portal: true },
    },
    {
      path: '/portal/settings',
      name: 'portal-settings',
      component: () => import('@/views/PortalSettingsView.vue'),
      meta: { portal: true },
    },
    // Unmatched portal URLs stay in the portal: authed users hit the dashboard,
    // the guard bounces unauthed ones to portal-login (never the admin login)
    { path: '/portal/:pathMatch(.*)*', redirect: { name: 'portal-dashboard' } },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
})

// Track whether initial auth check has been done
let authChecked = false
let portalAuthChecked = false

router.beforeEach(async (to) => {
  // Portal routes use their own auth system
  if (to.meta.portal) {
    if (to.meta.public) return

    const portalAuth = usePortalAuthStore()

    if (!portalAuthChecked) {
      try {
        await portalAuth.fetchCurrentUser()
      } catch {
        // Cookie invalid or expired
      }
      portalAuthChecked = true
    }

    if (!portalAuth.isAuthenticated) {
      return {
        name: 'portal-login',
        query: {
          redirect: to.fullPath,
          email: (to.query.email as string) || undefined,
        },
      }
    }

    return
  }

  // Public routes don't need auth check (except login redirect)
  if (to.meta.public && to.name !== 'login') return

  // Admin/internal routes - check auth via cookie (server call)
  const auth = useAuthStore()

  if (!authChecked) {
    try {
      await auth.fetchCurrentUser()
    } catch {
      // Cookie invalid or expired
    }
    authChecked = true
  }

  if (!auth.isAuthenticated && !to.meta.public) {
    return { name: 'login' }
  }

  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }

  if (to.meta.adminOnly) {
    if (auth.user && auth.user.role !== 'admin') {
      useUiStore().showToast('No tienes permisos para acceder a esta sección', 'error')
      return { name: 'dashboard' }
    }
  }

  if (to.meta.excludeViewer) {
    if (auth.user && auth.user.role === 'viewer') {
      useUiStore().showToast('No tienes permisos para acceder a esta sección', 'error')
      return { name: 'dashboard' }
    }
  }
})

router.afterEach((to) => {
  const pageName = typeof to.name === 'string' ? to.name : to.path
  trackPageView(pageName, to.fullPath, { is_portal: !!to.meta.portal })

  document.querySelector('main')?.scrollTo({ top: 0, behavior: 'instant' })
})

export default router
